import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ImagePlus, ZoomIn, ZoomOut, Move, Loader2, Users } from "lucide-react";

const FALLBACK = "https://static.prod-images.emergentagent.com/jobs/0c40222b-65cc-42fc-9117-09f0a7b6df59/images/b4c5f1b3db6edfa3fbde65d2b4dcc09f1974aa11d1d4ce106f7aca443babc598.jpeg";

export function GlobalBotAvatar() {
  const [current, setCurrent] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/global-bot-avatar"); setCurrent(data.url); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const src = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u) || FALLBACK;

  const resetCrop = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large. Maximum size is 5 MB."); return; }
    setPreview((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
    setFile(f);
    resetCrop();
  };

  const getCroppedBlob = (source, a) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const size = 512;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, a.x, a.y, a.width, a.height, 0, 0, size, size);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Crop failed"))), "image/jpeg", 0.92);
      };
      image.onerror = () => reject(new Error("Could not load image"));
      image.src = source;
    });

  const save = async () => {
    if (!file || !area) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(preview, area);
      const form = new FormData();
      form.append("file", new File([blob], "bull.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/admin/global-bot-avatar", form);
      toast.success(`Profile picture applied to all ${data.applied_to} accounts`);
      setPreview((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return null; });
      setFile(null); resetCrop();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-[#222] bg-[#0F0F0F] p-6 max-w-2xl space-y-5" data-testid="global-avatar-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 bg-white flex items-center justify-center"><Users className="h-5 w-5 text-black" /></div>
        <div>
          <div className="font-heading font-semibold">Global Bot Profile Picture</div>
          <div className="font-mono-term text-xs text-[#888]">Upload, crop &amp; zoom — applies to EVERY account's Smart Scalper at once</div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-full overflow-hidden border-2 shrink-0 flex items-center justify-center"
          style={{ borderColor: "#00F593", background: "radial-gradient(circle at 50% 40%, #1a0a0f, #04060d)" }}>
          <img src={src(current)} alt="current global avatar" data-testid="global-avatar-current" className="h-full w-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#888] leading-relaxed">Sa a se foto TOUT kliyan, manm ak staf ap genyen sou Smart Scalper la. Lè w chanje l epi sove, li chanje sou tout kont yo touswit.</p>
        </div>
      </div>

      {preview && (
        <>
          <div className="relative w-full h-64 bg-black rounded-2xl overflow-hidden" data-testid="global-avatar-cropper">
            <Cropper image={preview} crop={crop} zoom={zoom} minZoom={1} maxZoom={4} aspect={1}
              cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom}
              onCropComplete={(_, areaPixels) => setArea(areaPixels)} />
          </div>
          <div className="flex items-center gap-2 text-[#888] text-xs"><Move className="h-3.5 w-3.5" /> Trennen pou repozisyone · sèvi ak slider la pou agrandi/rezoum</div>
          <div className="flex items-center gap-3 w-full px-1">
            <ZoomOut className="h-4 w-4 text-[#888] shrink-0" />
            <Slider data-testid="global-avatar-zoom" value={[zoom]} min={1} max={4} step={0.05} onValueChange={([z]) => setZoom(z)} className="flex-1" />
            <ZoomIn className="h-4 w-4 text-[#888] shrink-0" />
          </div>
        </>
      )}

      <input id="global-avatar-file" data-testid="global-avatar-file" type="file" accept="image/*" onChange={pick} className="hidden" />
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="global-avatar-file" data-testid="global-avatar-choose"
          className="cursor-pointer inline-flex items-center justify-center gap-2 h-11 px-5 rounded-sm border border-[#333] bg-transparent text-white text-sm font-semibold hover:bg-[#1A1A1A]">
          <ImagePlus className="h-4 w-4" /> {preview ? "Chwazi yon lòt imaj" : "Chwazi Imaj"}
        </label>
        <Button onClick={save} disabled={!file || !area || uploading} data-testid="global-avatar-save"
          className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold px-6">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crop & Aplike sou Tout Kont"}
        </Button>
      </div>
      <p className="text-[11px] text-[#666]">JPG, PNG, WebP oswa GIF · max 5 MB</p>
    </div>
  );
}
